import useSWR from "swr";
import { ShareCategory } from "@dreambigger/shared/src/types";
import { api } from "@dreambigger/shared/src/api/acquire";
import { useAuthToken } from "../passwordless-auth";
import { useFetcher } from "./";

// All the paths for this api domain.
export const sharePaths = {
  getProductsByCategory: (financialInstitutionId: string) =>
    `/financial-institutions/${financialInstitutionId}/share-categories`,
};

/**
 * Hook to fetch share categories with authentication
 * Uses SWR for caching and automatic revalidation
 */
const useShares = (
  financialInstitutionId: string,
  onboardingApplicationId?: string
) => {
  const tokenHelper = useAuthToken(financialInstitutionId);
  const { fetchWithToken } = useFetcher(api, financialInstitutionId);

  // Build the path with optional query parameters
  const queryParams = onboardingApplicationId
    ? `?onboardingApplicationId=${onboardingApplicationId}`
    : "";

  // SWR key: [url, token] or null if token is expired
  const swrKey: [string, string] | null = tokenHelper.jwtIsExpired()
    ? null
    : [
        `${sharePaths.getProductsByCategory(
          financialInstitutionId
        )}${queryParams}`,
        tokenHelper.getJwtToken(),
      ];

  const { data, error, isValidating, mutate } = useSWR<ShareCategory[]>(
    swrKey,
    fetchWithToken,
    {
      revalidateOnFocus: false, // Don't refetch when window regains focus
      revalidateOnReconnect: false, // Don't refetch on reconnect
      shouldRetryOnError: false, // Don't retry on error (handle in UI)
    }
  );

  return {
    shareCategories: data,
    isLoading: !error && !data && isValidating,
    isValidating,
    error,
    mutate,
  };
};

export default useShares;
